import type { ReactElement, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3,
  Calendar,
  Home,
  LogOut,
  Menu,
  Plus,
  Settings,
  Target,
  User as UserIcon,
  UtensilsCrossed,
  X,
} from 'lucide-react';
import { useLogout, useProfile } from '@/lib/auth';
import { JobsBanner } from './JobsBanner';

/**
 * M3.0 — AppLayout premium dark.
 *
 * - Topbar sticky con backdrop blur fuerte sobre carbon-950 translúcido.
 *   Brand a la izquierda (con monograma "tm"), nav central, "+ Añadir" y
 *   menú avatar a la derecha.
 * - Glass cards en todo el shell; foco visible naranja.
 * - Active link: subrayado magma + glow sutil + texto mist-50.
 * - Drawer móvil: panel desde la izquierda con animación spring.
 *
 * Iconografía: Lucide consistente, 2px stroke, 16/20 px tamaños.
 * Tipografía: Inter en UI, Space Grotesk en brand mark.
 */

type NavItemDef = {
  to: '/' | '/entrenos' | '/analiticas' | '/coach/temporada' | '/nutricion';
  label: string;
  Icon: typeof Home;
};

const NAV_ITEMS: readonly NavItemDef[] = [
  { to: '/', label: 'Inicio', Icon: Home },
  { to: '/entrenos', label: 'Calendario', Icon: Calendar },
  { to: '/analiticas', label: 'Análisis', Icon: BarChart3 },
  { to: '/coach/temporada', label: 'Plan', Icon: Target },
  { to: '/nutricion', label: 'Nutrición', Icon: UtensilsCrossed },
] as const;

const NAV_LINK_BASE =
  'inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-mist-200 transition-all duration-200 hover:text-mist-50 hover:bg-white/5';
const NAV_LINK_ACTIVE =
  'inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-mist-50 bg-white/[0.06] shadow-[inset_0_-2px_0_rgb(255_94_19)]';

function getInitials(name: string): string {
  if (!name.trim()) return 'TM';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

export function AppLayout({ children }: { children: ReactNode }): ReactElement {
  const profileQuery = useProfile();
  const logoutMutation = useLogout();

  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onDocClick(ev: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(ev.target as Node)) {
        setMenuOpen(false);
      }
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!drawerOpen) return;
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') setDrawerOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  function handleLogout() {
    setMenuOpen(false);
    logoutMutation.mutate();
  }

  const displayName = profileQuery.data?.name ?? '';
  const initials = getInitials(displayName);

  return (
    <div className="grain relative min-h-screen bg-carbon-950 text-mist-50 antialiased">
      {/* Mesh gradient sutil global */}
      <div
        aria-hidden="true"
        className="bg-mesh-subtle pointer-events-none fixed inset-0 z-0"
      />

      <header
        data-testid="app-header"
        className="sticky top-0 z-40 border-b border-white/5 bg-carbon-950/80 backdrop-blur-xl"
      >
        <div className="mx-auto flex max-w-screen-2xl items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
          {/* Brand + hamb mobile */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="rounded-md p-2 text-mist-200 transition-colors hover:bg-white/5 hover:text-mist-50 lg:hidden"
              aria-label="Abrir menú"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
            <BrandLink />
          </div>

          {/* Nav central (desktop) */}
          <nav aria-label="Principal" className="hidden items-center gap-1 lg:flex">
            {NAV_ITEMS.map(({ to, label, Icon }) => (
              <Link
                key={to}
                to={to}
                className={NAV_LINK_BASE}
                activeProps={{ className: NAV_LINK_ACTIVE }}
                activeOptions={{ exact: to === '/' }}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                <span>{label}</span>
              </Link>
            ))}
          </nav>

          {/* Acciones derecha */}
          <div className="flex items-center gap-2">
            <Link
              to="/entrenos"
              className="group inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-magma-400 to-magma-600 px-3 py-2 text-sm font-semibold text-mist-50 shadow-[0_4px_12px_rgb(255_94_19_/_0.3)] transition-all hover:shadow-[0_6px_20px_rgb(255_94_19_/_0.5)] hover:-translate-y-px"
              title="Añadir actividad"
              aria-label="Añadir actividad"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Añadir</span>
            </Link>

            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="Menú de usuario"
                className="font-display inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-slate-tm-800/80 text-xs font-bold text-mist-50 transition-all hover:border-magma-500/40 hover:bg-slate-tm-750"
              >
                {initials}
              </button>
              <AnimatePresence>
                {menuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.98 }}
                    transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                    role="menu"
                    aria-label="Menú de usuario"
                    data-testid="user-menu"
                    className="glass-card-elev absolute right-0 mt-2 w-52 overflow-hidden py-1"
                  >
                    {displayName && (
                      <div className="border-b border-white/5 px-3 py-2 text-xs text-mist-300">
                        Sesión:{' '}
                        <span className="font-medium text-mist-50">{displayName}</span>
                      </div>
                    )}
                    <MenuLink to="/perfil" Icon={UserIcon} onClick={() => setMenuOpen(false)}>
                      Perfil
                    </MenuLink>
                    <MenuLink to="/ajustes" Icon={Settings} onClick={() => setMenuOpen(false)}>
                      Ajustes
                    </MenuLink>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleLogout}
                      disabled={logoutMutation.isPending}
                      aria-busy={logoutMutation.isPending}
                      className="flex w-full items-center gap-2.5 border-t border-white/5 px-3 py-2.5 text-sm text-mist-100 transition-colors hover:bg-white/[0.04] hover:text-red-400 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <LogOut className="h-4 w-4" aria-hidden="true" />
                      {logoutMutation.isPending ? 'Saliendo…' : 'Salir'}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>

      {/* Drawer móvil */}
      <AnimatePresence>
        {drawerOpen && (
          <div
            className="fixed inset-0 z-50 lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Menú de navegación"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-carbon-950/70 backdrop-blur-md"
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -20, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="glass-card-elev absolute left-4 top-4 h-[calc(100vh-2rem)] w-72 max-w-[85vw] overflow-y-auto p-4"
            >
              <div className="mb-6 flex items-center justify-between">
                <BrandLink size="sm" />
                <button
                  type="button"
                  className="rounded-md p-2 text-mist-200 hover:bg-white/5 hover:text-mist-50"
                  aria-label="Cerrar menú"
                  onClick={() => setDrawerOpen(false)}
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              <nav aria-label="Principal móvil" className="flex flex-col gap-1">
                {NAV_ITEMS.map(({ to, label, Icon }) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setDrawerOpen(false)}
                    className="inline-flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-mist-200 transition-colors hover:bg-white/5 hover:text-mist-50"
                    activeProps={{
                      className:
                        'inline-flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold bg-magma-500/10 text-mist-50 border-l-2 border-magma-500',
                    }}
                    activeOptions={{ exact: to === '/' }}
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {label}
                  </Link>
                ))}
              </nav>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <main
        id="main"
        className="relative z-10 mx-auto max-w-screen-2xl px-4 py-6 sm:px-6 lg:px-8 lg:py-10"
      >
        {children}
      </main>

      <JobsBanner />
    </div>
  );
}

/** Brand link con monograma "tm" gradiente magma. */
function BrandLink({ size = 'md' }: { size?: 'sm' | 'md' }): ReactElement {
  const dim = size === 'sm' ? 'h-7 w-7' : 'h-8 w-8';
  const text = size === 'sm' ? 'text-sm' : 'text-base';
  return (
    <Link
      to="/"
      className="inline-flex items-center gap-2.5 transition-opacity hover:opacity-90"
      title="Ir al inicio"
    >
      <span
        className={`${dim} relative grid place-items-center rounded-lg bg-gradient-to-br from-magma-400 via-magma-500 to-magma-700 shadow-[0_4px_12px_rgb(255_94_19_/_0.3),inset_0_1px_0_rgb(255_255_255_/_0.25)]`}
        aria-hidden="true"
      >
        <span className="font-display text-xs font-bold text-mist-50">tm</span>
      </span>
      <span className={`font-display font-bold tracking-tight text-mist-50 ${text}`}>
        tri-mentor
      </span>
    </Link>
  );
}

function MenuLink({
  to,
  Icon,
  onClick,
  children,
}: {
  to: '/perfil' | '/ajustes';
  Icon: typeof UserIcon;
  onClick: () => void;
  children: ReactNode;
}): ReactElement {
  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onClick}
      className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-mist-100 transition-colors hover:bg-white/[0.04] hover:text-mist-50"
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {children}
    </Link>
  );
}
